// AdRequestsReport.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Tooltip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';


// Label + colour of an ad request status (missing status = a new request)
const statusMeta = (status) => {
  switch (status) {
    case 'verified': return { label: 'Approved', color: 'success' };
    case 'accepted': return { label: 'Accepted', color: 'success' };
    case 'rejected': return { label: 'Rejected', color: 'error' };
    default: return { label: 'Pending', color: 'warning' };
  }
};

// Request date and time shown in the Active / Solved tables, e.g. "22 Oct 2026, 03:37 PM"
const formatRequestDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true
  });
};

const AdRequestsReport = () => {
  const [tabIndex, setTabIndex] = useState(0); // 0: Active / Verify, 1: History
  const [allAdRequests, setAllAdRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const { can } = usePermissions();
  const [userId, setUserId] = useState(null);
  const [error, setError] = useState('');

  // Dropdown / Filter States
  const [districtList, setDistrictList] = useState([]);
  const [districtFilter, setDistrictFilter] = useState('all');
  const [pincodeFilter, setPincodeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // View / Verify Modal State
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'verified' | 'rejected' | null
  const [decisionReason, setDecisionReason] = useState('');
  const [reasonError, setReasonError] = useState('');

  const token = sessionStorage.getItem('token');

  // Decode token
  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserId(decoded.userId);
      } catch (e) {
        console.error('Failed to decode token:', e);
      }
    }
  }, [token]);

  // Fetch Districts Dropdown
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/districts`);
        if (res.status === 200 && res.data) {
          const list = Array.isArray(res.data) ? res.data : res.data.districts || [];
          setDistrictList(list);
        }
      } catch (err) {
        console.error('Error fetching districts:', err);
      }
    };
    fetchDistricts();
  }, []);

  // Fetch Ad Requests
  const fetchAdRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/adRequests/all`, {
        params: { userId }
      });

      if (response.data && Array.isArray(response.data.adRequests)) {
        setAllAdRequests(response.data.adRequests);
      } else {
        setAllAdRequests([]);
      }
    } catch (err) {
      console.error('Error fetching ad requests:', err);
      setError('Failed to fetch advertisement requests.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchAdRequests();
  }, [fetchAdRequests]);

  // Filter Rows by Tab & Controls
  const getFilteredData = () => {
    return allAdRequests.filter((item) => {
      const isCreated = item.status === 'created' || !item.status;
      // Tab 0: Active (created), Tab 1: History (verified, accepted, rejected)
      if (tabIndex === 0 && !isCreated) return false;
      if (tabIndex === 1 && isCreated) return false;

      // District Filter
      if (districtFilter !== 'all') {
        if ((item.district || '').toLowerCase() !== districtFilter.toLowerCase()) {
          return false;
        }
      }

      // Pincode Filter
      if (pincodeFilter.trim()) {
        if (!String(item.pincode || '').includes(pincodeFilter.trim())) {
          return false;
        }
      }

      // Search Query Filter
      if (searchQuery.trim()) {
        const s = searchQuery.trim().toLowerCase();
        const matchName = (item.customerName || '').toLowerCase().includes(s);
        const matchId = (item.customCustId || item.customerId || '').toLowerCase().includes(s);
        const matchContact = (item.contactPerson || '').toLowerCase().includes(s);
        const matchPhone = (item.contactNumber || item.customerMobile || '').toLowerCase().includes(s);
        const matchPurpose = (item.purpose || '').toLowerCase().includes(s);

        if (!matchName && !matchId && !matchContact && !matchPhone && !matchPurpose) {
          return false;
        }
      }

      return true;
    });
  };

  const filteredRows = getFilteredData();

  // Reset Filters
  const handleResetFilters = () => {
    setDistrictFilter('all');
    setPincodeFilter('');
    setSearchQuery('');
  };

  // CSV Export (permission gated)
  const handleExportCSV = () => {
    if (!can('ads.requests.export')) return;
    if (filteredRows.length === 0) {
      alert('No data available to export.');
      return;
    }

    const isHistoryTab = tabIndex === 1;

    const headers = [
      'SL No',
      'Request Date & Time',
      'Customer/Merchant ID',
      'Customer/Merchant Name',
      'Ad Type',
      'Ad Purpose',
      'Ad Start Date',
      'Ad End Date',
      'Contact Person',
      'Contact Number',
      'Ad Content (Text)',
      'Description',
      'Status'
    ];

    const rows = filteredRows.map((row, idx) => [
      idx + 1,
      `"${formatRequestDateTime(row.createdAt)}"`,
      `"${row.customCustId || row.customerId || 'N/A'}"`,
      `"${row.customerName || 'N/A'}"`,
      `"${row.adType || row.requestType || 'N/A'}"`,
      `"${row.purpose || 'N/A'}"`,
      new Date(row.date || row.createdAt).toLocaleDateString('en-IN'),
      new Date(row.date ? new Date(row.date).setDate(new Date(row.date).getDate() + 7) : row.createdAt).toLocaleDateString('en-IN'),
      `"${row.contactPerson || row.customerName || 'N/A'}"`,
      `"${row.contactNumber || row.customerMobile || 'N/A'}"`,
      `"${row.content || 'N/A'}"`,
      `"${(row.remark || '').replace(/"/g, '""')}"`,
      `"${row.status || 'created'}"`
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute(
      'download',
      `ad_requests_${isHistoryTab ? 'history' : 'active'}_${new Date().toISOString().split('T')[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Open Details Modal
  const handleOpenDetails = (row) => {
    setSelectedAd(row);
    setDetailsModalOpen(true);
  };

  // Update Status (Verify / Reject)
  const handleUpdateStatus = async (newStatus) => {
    if (!can('ads.requests.approve')) return;
    if (!selectedAd) return;
    if (newStatus === 'rejected' && !decisionReason.trim()) {
      setReasonError('Please enter the reason for rejecting this request.');
      return;
    }
    setUpdatingStatus(true);
    try {
      await axios.put(`${API_BASE_URL}/adRequests/${selectedAd._id}/status`, {
        status: newStatus,
        reason: decisionReason.trim()
      }, { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } });

      setConfirmAction(null);
      setDecisionReason('');
      setReasonError('');
      setDetailsModalOpen(false);
      setSelectedAd(null);
      fetchAdRequests();
    } catch (err) {
      console.error('Error updating ad request status:', err);
      setConfirmAction(null);
      alert((err.response && err.response.data && err.response.data.message) || 'Failed to update ad request status.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <BackButton />
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', ml: 1 }}>
          Advertisement Requests (View & Verify)
        </Typography>
      </Box>

      {/* Tabs Header */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs
          value={tabIndex}
          onChange={(e, val) => setTabIndex(val)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab
            label={
              <Typography sx={{ fontWeight: 700, textTransform: 'none', fontSize: '1rem' }}>
                Active Ad Requests (View & Verify)
              </Typography>
            }
          />
          <Tab
            label={
              <Typography sx={{ fontWeight: 700, textTransform: 'none', fontSize: '1rem' }}>
                Verified & Past Ad Request History
              </Typography>
            }
          />
        </Tabs>
      </Paper>

      {/* Filter Controls Box */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        <Grid container spacing={2} alignItems="center">
          {/* District Wise */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by District</InputLabel>
              <Select
                value={districtFilter}
                label="Filter by District"
                onChange={(e) => setDistrictFilter(e.target.value)}
              >
                <MenuItem value="all">All Districts</MenuItem>
                {districtList.map((d) => {
                  const dName = d.districtName || d.name;
                  return (
                    <MenuItem key={d._id || dName} value={dName}>
                      {dName}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          {/* Pincode Filter */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Filter by Pincode"
              placeholder="e.g. 695001"
              value={pincodeFilter}
              onChange={(e) => setPincodeFilter(e.target.value)}
            />
          </Grid>

          {/* Search Query */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search Name / ID / Contact / Purpose"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                endAdornment: <SearchIcon color="action" fontSize="small" />
              }}
            />
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleResetFilters}
              startIcon={<RefreshIcon />}
              fullWidth
              size="medium"
            >
              Reset
            </Button>
            {can('ads.requests.export') && (
              <Button
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                fullWidth
                size="medium"
              >
                CSV
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Main Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 10px rgba(0,0,0,0.08)', p: 1, pr: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  SL No
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                  Request Date &amp; Time
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Customer / Merchant ID
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Customer / Merchant Name
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Ad Type
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Ad Purpose
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Description (Text Content)
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Ad Start / End Date
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Contact Person & Phone
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Status
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Action / Verify
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 3, color: '#64748b' }}>
                    No advertisement requests found matching criteria.
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.map((row, idx) => {
                  const startDateStr = new Date(row.date || row.createdAt).toLocaleDateString('en-IN');
                  const endDateObj = row.date ? new Date(row.date) : new Date(row.createdAt);
                  endDateObj.setDate(endDateObj.getDate() + 7);
                  const endDateStr = endDateObj.toLocaleDateString('en-IN');

                  return (
                    <TableRow key={row._id} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatRequestDateTime(row.createdAt)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#2563eb' }}>
                        {row.customCustId || row.customerId}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>
                        {row.customerName}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={(row.adType || row.requestType || 'Standard').toUpperCase()}
                          color="primary"
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ color: '#0284c7', fontWeight: 600 }}>
                        {row.purpose || 'Brand Promotion'}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 240, whiteSpace: 'normal', wordBreak: 'break-word', color: '#334155' }}>
                        {row.remark || row.description || (row.content && !row.content.startsWith('data:') && !row.content.startsWith('http') && row.content.length < 100 ? row.content : 'No extra description')}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.85rem' }}>
                        {startDateStr} to <br />
                        {endDateStr}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {row.contactPerson || row.customerName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.contactNumber || row.customerMobile || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={row.decisionReason || ''} disableHoverListener={!row.decisionReason}>
                          <Chip
                            label={statusMeta(row.status).label}
                            color={statusMeta(row.status).color}
                            size="small"
                            sx={{ fontWeight: 700 }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant={tabIndex === 0 ? 'contained' : 'outlined'}
                          color={tabIndex === 0 ? 'primary' : 'info'}
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleOpenDetails(row)}
                        >
                          {tabIndex === 0 ? 'Verify' : 'Details'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Details & Verify Dialog */}
      <Dialog open={detailsModalOpen} onClose={() => setDetailsModalOpen(false)} maxWidth="sm" fullWidth>
        {selectedAd && (
          <>
            <DialogTitle sx={{ fontWeight: 700, backgroundColor: '#1e293b', color: 'white' }}>
              Verify Advertisement Request
            </DialogTitle>
            <DialogContent dividers sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Show Customer / Merchant ID:
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                    {selectedAd.customCustId || selectedAd.customerId}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Show Customer / Merchant Name:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedAd.customerName}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Show Ad Type:
                  </Typography>
                  <Typography variant="body1" color="primary" fontWeight="bold">
                    {(selectedAd.adType || selectedAd.requestType || 'Standard').toUpperCase()}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Show Ad Purpose:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedAd.purpose || 'Brand Awareness'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Show Ad Start Date:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {new Date(selectedAd.date || selectedAd.createdAt).toLocaleDateString('en-IN')}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Show Ad End Date:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {new Date(selectedAd.date ? new Date(selectedAd.date).setDate(new Date(selectedAd.date).getDate() + 7) : selectedAd.createdAt).toLocaleDateString('en-IN')}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Show Contact Person:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedAd.contactPerson || selectedAd.customerName}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Show Contact Number:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedAd.contactNumber || selectedAd.customerMobile || 'N/A'}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Show Description / Remarks:
                  </Typography>
                  <Paper sx={{ p: 1.5, backgroundColor: '#f8fafc', mt: 0.5, border: '1px solid #e2e8f0' }}>
                    <Typography variant="body2">{selectedAd.remark || 'No extra remarks specified.'}</Typography>
                    {selectedAd.decisionReason && (
                      <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: selectedAd.status === 'rejected' ? '#fef2f2' : '#f0fdf4', border: '1px solid #e2e8f0' }}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary">
                          {selectedAd.status === 'rejected' ? 'REJECT REASON' : 'APPROVAL REASON'}
                          {selectedAd.decidedAt ? ` · ${new Date(selectedAd.decidedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}` : ''}
                        </Typography>
                        <Typography variant="body2">{selectedAd.decisionReason}</Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
              <Button onClick={() => setDetailsModalOpen(false)} disabled={updatingStatus}>
                Close
              </Button>
              {tabIndex === 0 && can('ads.requests.approve') && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={() => { setDecisionReason(''); setReasonError(''); setConfirmAction('rejected'); }}
                    disabled={updatingStatus}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => { setDecisionReason(''); setReasonError(''); setConfirmAction('verified'); }}
                    disabled={updatingStatus}
                  >
                    Verify & Approve
                  </Button>
                </Box>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Confirm approve / reject */}
      <Dialog open={!!confirmAction} onClose={() => !updatingStatus && setConfirmAction(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {confirmAction === 'rejected' ? 'Reject this ad request?' : 'Approve this ad request?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {selectedAd ? `Request from ${selectedAd.customerName || 'customer'}` : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {confirmAction === 'rejected'
              ? 'The request will be marked as rejected and moved to History.'
              : 'The request will be marked as approved and moved to History.'}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            sx={{ mt: 2 }}
            label={confirmAction === 'rejected' ? 'Reject reason (required)' : 'Approval reason (optional)'}
            value={decisionReason}
            onChange={(e) => { setDecisionReason(e.target.value); setReasonError(''); }}
            error={!!reasonError}
            helperText={reasonError || `${decisionReason.length}/500`}
            inputProps={{ maxLength: 500 }}
            disabled={updatingStatus}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setConfirmAction(null); setReasonError(''); }} disabled={updatingStatus}>Cancel</Button>
          <Button
            variant="contained"
            color={confirmAction === 'rejected' ? 'error' : 'success'}
            onClick={() => handleUpdateStatus(confirmAction)}
            disabled={updatingStatus}
          >
            {updatingStatus ? 'Please wait...' : (confirmAction === 'rejected' ? 'Yes, Reject' : 'Yes, Approve')}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default AdRequestsReport;
